<script lang="ts">
export default {
  name: 'KkApprovalHistoryModal',
}
</script>
<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm, useField } from 'vee-validate'
import type { FieldContext } from 'vee-validate'
import { KkApprovalHistory } from '../../../../domains/review'
import { KkModal, KkButton, KkBox, KkLoading, KkFormLabel, KkTextarea, KkValidationError } from '../../../../components/base'

interface ApprovalNote {
  commenter_name: string
  commented_at: string
  kind: 'approver_comment' | 'applicant_comment'
  body: string
}

interface Props {
  type: 'preview' | 'apply' | 'approve'
  isVisible: boolean,
  loading: boolean,
  approvalNotes?: ApprovalNote[]
}
const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
  loading: false,
  approvalNotes: () => []
})
const { t } = useI18n()
interface Emits {
  (e: 'close'): void
  (e: 'submit', value: string): void
}
const emit = defineEmits<Emits>()

const isPreview = computed<boolean>(() => props.type === 'preview')
const showSubmitButton = computed<boolean>(() => !isPreview.value)
const showCommentTextArea = computed<boolean>(() => !isPreview.value)
const submitText = computed<string>(() => props.type === 'apply' ? t('pages.CompaniesReviews.apply') : t('labels.confirm'))

const schema = {
  comment: 'max:400',
}

const { handleSubmit, meta } = useForm({
  validationSchema: schema,
  initialValues: {
    comment: '',
  },
})

const { value: comment, errorMessage: commentError, handleChange: commentHandleChange }: FieldContext<string> = useField('comment', undefined, { label: t('pages.CompaniesReviews.ApprovalHistoryModal.comment') })

const handlePrimaryButton = handleSubmit((value) => {
  emit('submit', value.comment)
})

const validation = computed<boolean>(() => meta.value.valid)
const disabled = computed<boolean>(() => props.loading ||  !validation.value)

</script>
<template>
  <KkModal class="approvalHistoryModal" width="720px" :is-visible="props.isVisible" @change="emit('close')">
    <KkBox :title="t('pages.CompaniesReviews.ApprovalHistoryModal.title')" gray-content content-height="56vh">
      <template #content>
        <KkLoading v-if="loading" />
        <div v-else>
          <KkApprovalHistory
            :approval-notes="props.approvalNotes"
          />
          <div v-if="showCommentTextArea" class="approvalComment">
            <KkFormLabel :text="`${ t('labels.approvalComment', { count: comment.length, max: 400 }) }`" />
            <KkTextarea :model-value="comment" placeholder="" @update:model-value="commentHandleChange" />
            <KkValidationError :text="commentError" />
          </div>
        </div>
      </template>
      <template #footer>
        <div class="buttonWrapper">
          <KkButton
            class="button"
            color="secondary"
            :text="isPreview ? t('labels.close') : t('labels.cancel')"
            @click="emit('close')"
          />
          <KkButton
            v-if="showSubmitButton"
            class="button"
            color="primary"
            :text="submitText"
            :disabled="disabled"
            @click="handlePrimaryButton"
          />
        </div>
      </template>
    </KkBox>
  </KkModal>
</template>
<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';
.approvalComment {
  margin-top: 32px;
}
.buttonWrapper {
  display: flex;
  justify-content: flex-end;
}
.button {
  margin-left: 8px;
}
</style>
